import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  List,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { CheckOutlined, CrownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAgentBillingStatus } from "../../contexts/AgentBillingStatusContext";
import { useAppContext } from "../../contexts/AppContext";
import {
  getAgentSubscriptionUpgradeOptions,
  postAgentSubscriptionUpgradePackage,
} from "../../services/agentSubscriptionUpgradeApi";
import {
  openAgentPaymentMethodSetup,
  resolveAgentBillingSetupRedirectUrl,
  startAgentBillingSetup,
} from "../../services/agentBillingStatusApi";
import { AGENT_PACKAGE_UPGRADE_PATH } from "../../utils/agentUpgradeNavigation";
import { buildTrialConversionCreditRequest } from "../../utils/agentTrialRegistration";
import { formatPackageSubscriptionGrossIls } from "../../utils/resolvePackagePriceWithVat";

const { Title, Paragraph, Text } = Typography;

const FEATURE_LABEL_HE = {
  leads_system: "מערכת לידים",
  crazy_llama: "צ׳אט לקוחות / AI Sales",
  monthly_pricing: "מחירון חודשי",
  customer_offer_link: "קישור הצעת מחיר ללקוח",
  multi_package_customer_offer: "הצעת השוואת חבילות ללקוח",
  search_hotels: "חיפוש מלונות",
  search_packages: "חיפוש חבילות נופש",
  search_flights: "חיפוש טיסות",
  dynamic_packages: "חבילות דינמיות",
  organized_tours: "טיולים מאורגנים",
  team_management: "ניהול צוות סוכנות",
  priority_support: "תמיכה בעדיפות",
  seo_system: "מערכת SEO לאתר",
  smart_quotes: "הצעות חכמות",
  hotel_prebooking: "פרה-בוקינג מלון",
  referral_rewards: "תגמולי הפניה",
  max_monthly_orders: "תקרת הזמנות חודשית",
  commission_percentage: "אחוז עמלה",
  hotel_hr_commission_percentage: "עמלת Hotels River",
};

function formatIls(netAmount) {
  return formatPackageSubscriptionGrossIls(netAmount);
}

function labelForFeatureKey(key) {
  const k = String(key || "").trim();
  return FEATURE_LABEL_HE[k] || k;
}

export default function AgentPackageUpgradePage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { refreshAgentEntitlements } = useAppContext();
  const { reload: reloadBillingStatus } = useAgentBillingStatus();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  /** שלילת OPTIONS בגלל legacy_unbilled — לא מנסים שוב בהעדר מנוי */
  const [legacyBillingBlocked, setLegacyBillingBlocked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAgentSubscriptionUpgradeOptions();
      if (data?.success === false) {
        setPayload(null);
        setLegacyBillingBlocked(data?.error?.code === "LEGACY_UNBILLED");
        setError(data?.error?.message || "טעינה נכשלה");
        return;
      }
      setLegacyBillingBlocked(false);
      setPayload(data);
    } catch (e) {
      setPayload(null);
      const apiCode = e?.response?.data?.error?.code;
      setLegacyBillingBlocked(apiCode === "LEGACY_UNBILLED");
      setError(e?.response?.data?.error?.message || e?.message || "טעינה נכשלה");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const targets = useMemo(() => (Array.isArray(payload?.targets) ? payload.targets : []), [payload]);

  const onUpgrade = useCallback(
    async (code) => {
      const c = String(code || "").trim();
      if (!c) return;
      if (import.meta.env.DEV) {
        console.log("[UPGRADE_PACKAGE_REQUEST]", {
          route: AGENT_PACKAGE_UPGRADE_PATH,
          target_package_code: c,
          effective_from: "start_of_current_month",
        });
      }
      setSubmitting(true);
      try {
        const res = await postAgentSubscriptionUpgradePackage({
          target_package_code: c,
          effective_from: "start_of_current_month",
          ...buildTrialConversionCreditRequest({
            currentPackageCode: payload?.current?.code,
            targetPackageCode: c,
          }),
        });
        if (res?.success === false) {
          message.error(res?.error?.message || "שינוי החבילה נכשל");
          return;
        }
        const morningLink =
          res?.morningPaymentLink ||
          res?.morning_payment_link ||
          res?.paymentLinkUrl ||
          null;
        if (
          (res?.requiresMorningStandingOrder === true ||
            res?.requiresPaymentLink === true) &&
          morningLink
        ) {
          try {
            sessionStorage.setItem("hr_subscription_payment_package", c);
          } catch {
            /* ignore */
          }
          message.info(`יש להקים הוראת קבע חדשה ל-${c}. מעבירים ל-Morning…`);
          window.location.assign(morningLink);
          return;
        }
        if (res?.requiresMorningStandingOrder === true || res?.requiresPaymentLink === true) {
          const fallback = openAgentPaymentMethodSetup({ packageCode: c });
          const setupUrl = resolveAgentBillingSetupRedirectUrl(fallback);
          if (setupUrl && fallback?.ok !== false) {
            message.info(`יש להקים הוראת קבע חדשה ל-${c}. מעבירים ל-Morning…`);
            window.location.assign(setupUrl);
            return;
          }
          message.error("לא ניתן לפתוח את לינק Morning. נסו שוב.");
          return;
        }
        if (res?.noOp === true) {
          message.info("אתם כבר בחבילה זו");
          return;
        }
        if (import.meta.env.DEV) {
          console.log("[UPGRADE_PACKAGE_SUCCESS]", {
            targetPackageCode: res?.targetPackageCode,
            message: res?.message,
          });
        }
        try {
          await refreshAgentEntitlements?.();
          if (import.meta.env.DEV) {
            console.log("[ENTITLEMENTS_REFRESH_AFTER_UPGRADE]", { ok: true });
          }
        } catch {
          /* non-fatal */
        }
        try {
          await reloadBillingStatus?.();
        } catch {
          /* non-fatal */
        }
        message.success("בקשת שינוי החבילה נקלטה — ממתינים לאישור Admin");
        navigate("/p/dashboard/management", { replace: false });
      } catch (e) {
        const msg = e?.response?.data?.error?.message || e?.message || "שינוי החבילה נכשל";
        message.error(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [message, navigate, payload?.current?.code, refreshAgentEntitlements, reloadBillingStatus]
  );

  const onBillingSetup = useCallback(async () => {
    try {
      const data = await startAgentBillingSetup();
      const url = data?.setupUrl;
      if (!url) {
        message.error("לא ניתן לפתוח דף תשלום");
        return;
      }
      window.location.assign(url);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "שגיאה בפתיחת הסדרת תשלום");
    }
  }, [message]);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }} dir="rtl">
        <Spin size="large" />
        <Paragraph type="secondary" style={{ marginTop: 16 }}>
          טוען אפשרויות שדרוג…
        </Paragraph>
      </div>
    );
  }

  return (
    <div className="agent-package-upgrade-page" dir="rtl" style={{ maxWidth: 960, margin: "0 auto" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Button type="link" onClick={() => navigate("/p/dashboard/management")} style={{ padding: 0 }}>
          חזרה לאזור האישי
        </Button>

        <Title level={2} style={{ margin: 0 }}>
          שינוי חבילה
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          בוחרים חבילה חדשה — נדרש להקים הוראת קבע חדשה ב-Morning. החבילה תעודכן רק לאחר אישור Admin.
        </Paragraph>

        {error && (
          <Alert
            type="warning"
            showIcon
            message="לא ניתן לטעון את אפשרויות השדרוג"
            description={error}
            action={
              <Button size="small" onClick={load}>
                נסה שוב
              </Button>
            }
          />
        )}

        {legacyBillingBlocked && (
          <Alert
            type="info"
            showIcon
            message="נדרשת הסדרת אמצעי תשלום"
            description="לפני שדרוג חבילה יש להשלים את מסלול החיוב באזור האישי."
            action={
              <Button type="primary" size="small" onClick={onBillingSetup}>
                הסדרת אמצעי תשלום
              </Button>
            }
          />
        )}

        {payload?.current ? (
          <Card title="החבילה הנוכחית" size="small">
            <Space direction="vertical">
              <Text strong style={{ fontSize: 18 }}>
                {payload.current.name} ({payload.current.code})
              </Text>
              <Text type="secondary">מחיר חודשי משוער: {formatIls(payload.current.monthlyAmountIls)}</Text>
            </Space>
          </Card>
        ) : null}

        <Title level={4}>חבילות זמינות</Title>

        {targets.length === 0 && !error ? (
          <Alert type="success" showIcon message="אין חבילות נוספות" description="אין חבילות אחרות לבחירה כרגע." />
        ) : null}

        <Row gutter={[16, 16]}>
          {targets.map((t) => {
            const isPro = String(t.code).toUpperCase() === "PRO";
            return (
              <Col xs={24} md={12} key={t.code}>
                <Card
                  hoverable={!submitting}
                  size="small"
                  style={isPro ? { border: "2px solid #fa8c16" } : undefined}
                  title={
                    <Space>
                      {isPro ? <CrownOutlined /> : null}
                      <span>{t.name}</span>
                      {t.recommended ? <Tag color="gold">מומלץ</Tag> : null}
                    </Space>
                  }
                  extra={<Text strong>{formatIls(t.monthlyAmountIls)}</Text>}
                >
                  <List
                    size="small"
                    dataSource={Array.isArray(t.featureKeysEnabled) ? t.featureKeysEnabled : []}
                    locale={{ emptyText: "לא הוחזרו פיצ׳רים מתוך המסד — עדיין אפשר לשדרג" }}
                    renderItem={(item) => (
                      <List.Item style={{ padding: "4px 0", borderBlockEnd: "none" }}>
                        <CheckOutlined style={{ color: "#52c41a", marginLeft: 8 }} />
                        <span>{labelForFeatureKey(item)}</span>
                      </List.Item>
                    )}
                  />
                  <Button
                    type="primary"
                    block
                    style={{ marginTop: 12 }}
                    loading={submitting}
                    onClick={() => onUpgrade(t.code)}
                  >
                    {`הקם הוראת קבע חדשה ל-${t.code}`}
                  </Button>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Space>
    </div>
  );
}
